import { useState, useEffect, useRef } from "react";
import { Send, Trash2, Bot } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const AssistantChat = ({ authToken }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [provider, setProvider] = useState('openai');
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ message: userMsg.content, provider }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          provider: data.provider,
          model: data.model,
          is_mock: data.is_mock,
          timestamp: new Date().toISOString(),
        }]);
      }
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-340px)] bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden" data-testid="ai-assistant-chat">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-cyan-400" />
          <h3 className="text-white font-semibold text-sm">AI Assistant</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">MOCK</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={provider} onChange={e => setProvider(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white text-xs focus:outline-none"
            data-testid="ai-assistant-provider-select">
            <option value="openai" className="bg-gray-900">OpenAI</option>
            <option value="gemini" className="bg-gray-900">Gemini</option>
            <option value="anthropic" className="bg-gray-900">Anthropic</option>
            <option value="local" className="bg-gray-900">Local</option>
          </select>
          {messages.length > 0 && (
            <button onClick={() => setMessages([])} className="text-white/30 hover:text-red-400 transition" data-testid="ai-assistant-clear">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="flex-1 flex items-center justify-center h-full">
            <div className="text-center">
              <Bot className="w-12 h-12 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">Ask the AI assistant anything about your station</p>
              <p className="text-white/15 text-xs mt-1">Responses are mocked until AI providers are configured</p>
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
              msg.role === 'user'
                ? 'bg-cyan-600/20 text-white border border-cyan-500/20 rounded-br-sm'
                : 'bg-white/5 text-white/80 border border-white/10 rounded-bl-sm'
            }`}>
              {msg.role === 'assistant' && msg.is_mock && (
                <span className="text-[10px] text-amber-400/60 block mb-1">Mock Response ({msg.provider}/{msg.model})</span>
              )}
              <p className="break-words whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-cyan-400/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-cyan-400/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-cyan-400/50 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-white/10 px-4 py-3 flex gap-2">
        <input
          type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder="Ask the AI assistant..."
          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-500/50"
          data-testid="ai-assistant-input"
        />
        <button onClick={sendMessage} disabled={sending || !input.trim()}
          className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white px-4 py-2.5 rounded-xl transition flex items-center gap-2 text-sm"
          data-testid="ai-assistant-send">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
